import React from 'react';

import { Box } from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicOffIcon from '@mui/icons-material/MicOff';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import { ReactComponent as DroneMode } from '@public/images/DroneMode.svg';
import { ReactComponent as LevelEditor } from '@public/images/LevelEditor.svg';
import IconPopover from '../../../IconPopover';
import ToolBoxItem from '../../../ToolBoxItem';

import { useAdminTools } from './hooks';

import { useStyles } from './styles';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { env } from 'env';
import { COLORS } from '@constants/colors';
import EventsService from '@services/EventsService';

const { REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE } = env;

const AdminTools = () => {
  const {
    translate,
    handleCreatePOIs,
    handleDroneMode,
    handleMuteAll,
    handleLevelEditor,
  } = useAdminTools();

  const classes = useStyles();

  const {
    currentEvent,
    currentRoom,
    setRoomSpots,
    setSelectedCustomizationUeId,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const [isSpotsEditorMode, setIsSpotsEditorMode] = React.useState(false);

  const isLevelEditingEnabled =
    currentEvent?.world?.isLevelEditingEnabled ?? false;

  const handleToggleSpotsEditor = async () => {
    const nextMode = !isSpotsEditorMode;

    if (nextMode && currentRoom) {
      const roomSpots = await EventsService.getRoomSpots(currentRoom.roomId);
      setRoomSpots(roomSpots);
      setSelectedCustomizationUeId(null);
    }

    setIsSpotsEditorMode(nextMode);
    sendMessageToUnreal({
      action: nextMode
        ? 'ENTER_TO_CUSTOMIZATION_MODE'
        : 'LEAVE_CUSTOMIZATION_MODE',
    });
  };

  const spotsEditorIcon = (
    <DesignServicesIcon
      sx={{
        color: isSpotsEditorMode ? COLORS.MYTAVERSE_BLUE : 'white',
        transition: 'color 0.2s ease',
      }}
    />
  );

  return (
    <IconPopover
      icon={<ManageAccountsIcon sx={classes.icon} />}
      label={`${translate('adminToolbox.toolbox')} admin`}
      position={{ vertical: 'top', horizontal: 'right' }}
      analyticsName="admin"
    >
      {({ onClose }) => (
        <Box component="div" sx={classes.container}>
          <Box component="div" sx={classes.tools}>
            <ToolBoxItem
              Icon={<UploadFileIcon />}
              title={translate('adminToolbox.createPOIs')}
              handleClick={handleCreatePOIs}
            />
            <ToolBoxItem
              Icon={spotsEditorIcon}
              title={
                isSpotsEditorMode
                  ? translate(
                      'adminToolbox.spotsEditorActive',
                      'Spots editor (on)',
                    )
                  : translate('adminToolbox.spotsEditor', 'Spots editor')
              }
              handleClick={handleToggleSpotsEditor}
            />
            <ToolBoxItem
              Icon={<DroneMode />}
              title={translate('adminToolbox.droneMode')}
              handleClick={handleDroneMode}
            />
            {REACT_APP_SHOW_MUTE_ALL_IN_CONFERENCE === 'true' ? (
              <ToolBoxItem
                Icon={<MicOffIcon />}
                title={translate('adminToolbox.muteAll')}
                handleClick={handleMuteAll}
              />
            ) : null}
            {isLevelEditingEnabled ? (
              <ToolBoxItem
                Icon={<LevelEditor />}
                title={translate('adminToolbox.levelEditor')}
                handleClick={() => {
                  handleLevelEditor();
                  onClose();
                }}
              />
            ) : null}
          </Box>
        </Box>
      )}
    </IconPopover>
  );
};

export default AdminTools;
